<template>
    <v-dialog
    v-model="shown"
    max-width="600"
  >
    <v-card v-if="invoice">
      <v-card-text v-if="loading" style="height: 241px; padding: 1px;">
        <AppSpinner />
      </v-card-text>
      <v-card-text v-else>
        <v-row class="preview">
          <InvoicePreview class="invoice-component" :invoice="fullInvoice" />
        </v-row>
        <v-row>
          <v-col cols="6" class="pb-0" v-if="canBeChangedOrCanceled">
            <v-btn
              :disabled="!permission_cms_invoice_history_update"
              color="#0F998E"
              class="white--text"
              style="width: 100%"
              @click="$emit('changeInvoice')"
            >
              Spremeni
            </v-btn>
          </v-col>
          <v-col cols="6" class="pb-0" v-if="canBeChangedOrCanceled">
            <v-btn
              :disabled="!permission_cms_invoice_history_revert"
              color="#0F998E"
              class="white--text"
              style="width: 100%"
              @click="$emit('cancelInvoice')"
            >
              Storno
            </v-btn>
          </v-col>
          <v-col cols="6">
            <v-btn
              color="#ED7F78"
              class="white--text"
              style="width: 100%"
              @click="shown = false"
            >
              Zapri
            </v-btn>
          </v-col>
          <v-col cols="6">
            <v-btn
              :disabled="!permission_cms_invoice_history_print_or_copy"
              color="#2196f3"
              class="white--text"
              style="width: 100%"
              @click="print"
            >
              Tiskaj (A4/PDF)
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import BigNumber from 'bignumber.js'
import moment from 'moment'
import InvoicePreview from './InvoicePreview'
import { mapActions, mapGetters } from 'vuex'

export default {
  components: {
    InvoicePreview
  },
  props: {
    invoice: {
      required: true,
      validator: prop => typeof prop === 'object' || prop === null
    }
  },
  data () {
    return {
      loading: true,
      fullInvoice: {},
      shown: false
    }
  },
  computed: {
    ...mapGetters('auth', ['user']),
    appropriateUserCmsPermissions () {
      const userSettings = Object.values(this.userTypes).find(typ => typ.id === this.user.user_type)
      if (userSettings && userSettings.settings) {
        return userSettings.settings.filter(setting => setting.relevance === 1)
      }
      return []
    },
    userTypes () {
      return this.$store.state.userTypes.items
    },
    permission_cms_invoice_history_update () {
      return this.permissionCheck('permission_cms_invoice_history_update')
    },
    permission_cms_invoice_history_revert () {
      return this.permissionCheck('permission_cms_invoice_history_revert')
    },
    permission_cms_invoice_history_print_or_copy () {
      return this.permissionCheck('permission_cms_invoice_history_print_or_copy')
    },
    company () {
      return this.$store.state.companies.myCompany
    },
    businessUnit () {
      return this.$store.state.businessUnits.currentBusinessUnit
    },
    canBeChangedOrCanceled () {
      return this.fullInvoice && this.fullInvoice.invoice_amount >= 0 && this.fullInvoice.reverse_invoice === null
    }
  },
  methods: {
    ...mapActions('companies', ['getClient']),
    ...mapActions('devices', ['getHeader', 'getFooter']),
    ...mapActions('invoices', ['getPrints']),
    ...mapActions('userTypes', ['loadItems']),
    permissionCheck (permission_key) {
      if (this.appropriateUserCmsPermissions && this.appropriateUserCmsPermissions.length) {
        const found_permission = this.appropriateUserCmsPermissions.find(per => per.key === permission_key)
        if (found_permission) {
          return found_permission.value === 'true'
        }
      }
      return false
    },
    selectType (id) {
      this.selectedTypeId = id
    },
    close () {
      this.shown = false
    },
    open () {
      this.loading = true
      this.addDataToInvoice()
        .then(fullInvoice => {
          this.fullInvoice = fullInvoice
          this.$emit('fillOutInvoice', fullInvoice)
          this.loading = false
        })
      this.shown = true
    },
    updateFullInvoice (change) {
      this.fullInvoice = { ...this.fullInvoice, ...change }
    },
    print () {
      this.shown = false
      this.$emit('print')
    },
    addDataToInvoice () {
      return new Promise((resolve, reject) => {
        const requests = [
          this.getHeader({ electronicDeviceId: this.invoice.electronic_device }),
          this.getFooter({ electronicDeviceId: this.invoice.electronic_device }),
          this.getPrints(this.invoice)
        ]
        if (this.invoice.client) {
          requests.push(this.getClient({ clientId: this.invoice.client, invoiceId: this.invoice.id }))
        }
        Promise.all(requests)
          .then((results) => {
            const fullInvoice = { ...this.invoice }
            fullInvoice.company = this.company
            fullInvoice.businessUnit = this.businessUnit
            fullInvoice.invoiceFullNum = invoiceFullNum(this.invoice, this.businessUnit)
            fullInvoice.formattedDate = moment(this.invoice.date).format('DD.MM.YYYY HH:mm:ss')
            fullInvoice.discountTotal = calculateTotalDiscount(this.invoice.articles)
            fullInvoice.qrCodeData = qrCodeString(this.invoice.zoi, this.invoice.date, this.company.tax_number)
            fullInvoice.taxGroups = taxGroups(this.invoice.articles)
            if (this.invoice.client) {
              fullInvoice.buyerInfo = results[3]
            }
            fullInvoice.header = results[0]
            fullInvoice.footer = results[1]
            fullInvoice.printDetails = results[2]
            fullInvoice.printNumber = getLastInvoiceCopyNumber(results[2])
            fullInvoice.invoiceCopyNumber = fullInvoice.printNumber
            resolve(fullInvoice)
          })
      })
    }
  },
  created () {
    this.loadItems()
  }
}

const getLastInvoiceCopyNumber = printDetails => {
  if (!printDetails.length) return 1
  const maxPrintNum = Math.max.apply(Math, printDetails.map(print => { return print.index }))
  return maxPrintNum + 1
}

const calculateTotalDiscount = articles => {
  let discount = 0
  articles.forEach(item => {
    let itemPrice = item.price * item.quantity
    if (item.discount) discount += itemPrice * (item.discount / 100)
  })
  return roundedFloat(discount)
}

const invoiceFullNum = (invoice, businessUnit) => {
  return `${businessUnit.business_premise_id}-${invoice.electronic_device_id}-${invoice.number}`
}

const roundedFloat = number => {
  return Number.parseFloat(Number.parseFloat((number + '').replace(',', '.')).toFixed(2))
}

const qrCodeString = (zoi, date, taxNum) => {
  const zoiDec = BigNumber(zoi, 16).toString(10)
  const formattedDate = moment(date).format('YYMMDDHHmmss')
  const firstPart = `${zoiDec}${taxNum}${formattedDate}`
  let firstPartSum = 0
  for (let i = 0; i < firstPart.length; i++) {
    let num = parseInt(firstPart[i], 10)
    firstPartSum += num
  }
  const control = firstPartSum % 10
  return `${firstPart}${control}`
}

const taxGroups = articles => {
  const groups = {}
  articles.forEach(article => {
    const key = article.tax_level_value.toString()
    if (groups[key]) {
      groups[key].taxable_amount += article.taxable_amount
      groups[key].tax_amount += article.tax_amount
      groups[key].price += article.total_amount
    } else {
      groups[key] = {
        tax_level_value: article.tax_level_value,
        taxable_amount: article.taxable_amount,
        tax_amount: article.tax_amount,
        price: article.total_amount
      }
    }
  })
  return Object.values(groups)
}

</script>

<style lang="scss" scoped>
.preview {
  padding-top: 15px;
  max-height: calc(90vh - 130px);
  overflow: auto;
  .invoice-component {
    margin: 0 auto;
  }
}
</style>
